import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckSquare, FiChevronDown, FiFilter, FiSquare, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function compactNumber(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  const rounded = Math.round(number);
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
}

function formatMoney(value) {
  return `${compactNumber(value)} €`;
}

function compactMoney(value) {
  const number = Number(value || 0);
  if (!number) return "-";
  return compactNumber(number);
}

function formatPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 }).format(number)}%`;
}

function sourceLabel(source) {
  if (source === "actual") return "Actual";
  if (source === "forecast") return "Forecast";
  if (source === "mixed") return "Mixed";
  return "-";
}

function sourceClass(source) {
  if (source === "actual") return "analyticsSourceBadge--actual";
  if (source === "forecast") return "analyticsSourceBadge--forecast";
  if (source === "mixed") return "analyticsSourceBadge--mixed";
  return "analyticsSourceBadge--empty";
}

const TAXONOMY_LEVELS = [
  { value: "family", label: "Family", plural: "families" },
  { value: "subfamily", label: "Subfamily", plural: "subfamilies" },
  { value: "category", label: "Category", plural: "categories" },
  { value: "subcategory", label: "Subcategory", plural: "subcategories" },
];

function levelMeta(level) {
  return TAXONOMY_LEVELS.find((item) => item.value === level) || TAXONOMY_LEVELS[0];
}

export default function CostControl() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [salesReport, setSalesReport] = useState(null);
  const [month, setMonth] = useState(currentMonth);
  const [taxonomyLevel, setTaxonomyLevel] = useState("family");
  const [selectedTaxonomies, setSelectedTaxonomies] = useState([]);
  const [taxonomySearch, setTaxonomySearch] = useState("");
  const [taxonomyFilterOpen, setTaxonomyFilterOpen] = useState(false);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState("");
  const taxonomyIdsParam = selectedTaxonomies.join(",");

  const loadSalesReport = useCallback(async () => {
    try {
      setSalesLoading(true);
      setSalesError("");
      const res = await axios.get("/api/analytics/sales/net-by-family-day", {
        params: {
          business_unit_id: businessUnitId,
          month,
          taxonomy_level: taxonomyLevel,
          taxonomy_ids: taxonomyIdsParam,
        },
      });
      setSalesReport(res.data || null);
    } catch (err) {
      setSalesError(err?.response?.data?.error || "Could not load daily margin.");
    } finally {
      setSalesLoading(false);
    }
  }, [businessUnitId, taxonomyIdsParam, month, taxonomyLevel]);

  useEffect(() => {
    loadSalesReport();
  }, [loadSalesReport]);

  const selectedTaxonomySet = useMemo(() => new Set(selectedTaxonomies), [selectedTaxonomies]);
  const days = salesReport?.days || [];
  const marginRows = salesReport?.rows || [];
  const taxonomyOptions = useMemo(() => salesReport?.taxonomy_options || salesReport?.families || [], [salesReport]);
  const totals = salesReport?.totals || {};
  const totalsByDay = salesReport?.totals_by_day || {};
  const costCriterion = salesReport?.cost_criterion || {};
  const missingVatLines = Number(salesReport?.warnings?.missing_vat_lines || 0);
  const currentLevel = levelMeta(taxonomyLevel);
  const visibleTaxonomyLabel =
    selectedTaxonomies.length === 0 ? `All ${currentLevel.plural}` : `${selectedTaxonomies.length} ${currentLevel.plural}`;
  const filteredTaxonomyOptions = useMemo(() => {
    const query = taxonomySearch.trim().toLowerCase();
    if (!query) return taxonomyOptions;
    return taxonomyOptions.filter((item) => {
      const haystack = `${item.name || ""} ${item.code || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [taxonomyOptions, taxonomySearch]);

  const toggleTaxonomy = (key) => {
    setSelectedTaxonomies((current) => {
      if (current.includes(key)) return current.filter((item) => item !== key);
      return [...current, key];
    });
  };

  const selectAllTaxonomies = () => {
    setSelectedTaxonomies([]);
  };

  const clearTaxonomies = () => {
    setSelectedTaxonomies([]);
  };

  const handleTaxonomyLevelChange = (event) => {
    setTaxonomyLevel(event.target.value || "family");
    setSelectedTaxonomies([]);
    setTaxonomySearch("");
  };

  return (
    <div className="reservations-container rm-page analyticsCostPage">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Cost control</h3>
            <div className="rm-rates-subtitle">
              {salesLoading ? "Loading daily margin..." : `${visibleTaxonomyLabel} / ${month}`}
            </div>
          </div>

          <div className="rm-topActions">
            <select className="posTextInput analyticsLevelSelect" value={taxonomyLevel} onChange={handleTaxonomyLevelChange}>
              {TAXONOMY_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
            <div className="analyticsTaxonomyFilter">
              <button
                type="button"
                className={`rm-topBtn ${selectedTaxonomies.length ? "rm-topBtn--violet" : ""}`}
                onClick={() => setTaxonomyFilterOpen((open) => !open)}
              >
                <FiFilter /> Taxonomy <FiChevronDown />
              </button>
              {taxonomyFilterOpen ? (
                <div className="analyticsTaxonomyPopover">
                  <input
                    className="posTextInput"
                    value={taxonomySearch}
                    onChange={(event) => setTaxonomySearch(event.target.value)}
                    placeholder={`Search ${currentLevel.plural}`}
                  />
                  <div className="analyticsFamilyGrid analyticsFamilyGrid--compact analyticsTaxonomyPickerGrid">
                    {filteredTaxonomyOptions.map((taxonomy) => {
                      const key = String(taxonomy?.id || "");
                      const selected = selectedTaxonomies.length === 0 || selectedTaxonomySet.has(key);
                      return (
                        <button
                          type="button"
                          key={key}
                          className={`analyticsFamilyChip ${selected ? "analyticsFamilyChip--selected" : ""}`}
                          onClick={() => toggleTaxonomy(key)}
                        >
                          {selected ? <FiCheckSquare /> : <FiSquare />}
                          <span>{taxonomy.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="analyticsTaxonomyPopoverActions">
                    <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={selectAllTaxonomies}>
                      <FiCheckSquare /> Show all
                    </button>
                    <button
                      type="button"
                      className="rm-topBtn"
                      onClick={clearTaxonomies}
                      disabled={!selectedTaxonomies.length}
                    >
                      <FiX /> Clear filter
                    </button>
                    <button type="button" className="rm-topBtn" onClick={() => setTaxonomyFilterOpen(false)}>
                      Done
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            <input
              className="posTextInput analyticsMonthInput"
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value || currentMonth())}
            />
          </div>
        </div>
      </div>

      {salesError ? <div className="posStateBanner">{salesError}</div> : null}
      {missingVatLines ? (
        <div className="posStateBanner analyticsWarningBanner">
          {compactNumber(missingVatLines)} sales lines have products without IVA. Net sales are incomplete for those lines.
        </div>
      ) : null}

      <div className="analyticsCostCriterion">
        <div>
          <strong>Cost criterion</strong>
          <span>
            Daily cost uses inventory consumption:{" "}
            <b>{costCriterion.operational_cost_basis || "opening_inventory_value + purchase_inventory_value - closing_inventory_value"}</b>.
          </span>
        </div>
        <div>
          <strong>Purchases</strong>
          <span>
            Net purchases include purchase entries and subtract supplier returns before comparing opening and closing inventory.
          </span>
        </div>
        <div>
          <strong>Count differences</strong>
          <span>
            Inventory count differences affect closing inventory, so they are included in daily cost and shown separately.
          </span>
        </div>
        <div>
          <strong>Direct Consumption</strong>
          <span>Direct Consumption purchases create a same-day output, so the formula recognizes them as same-day cost.</span>
        </div>
      </div>

      <div className="posMetricGrid analyticsMetricGrid">
        <div className="posMetricTile">
          <span>Net sales</span>
          <strong>{formatMoney(totals.net_sales)}</strong>
        </div>
        <div className="posMetricTile">
          <span>PVP sales</span>
          <strong>{formatMoney(totals.gross_sales)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Daily cost</span>
          <strong>{formatMoney(totals.purchase_cost)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Count differences</span>
          <strong>{formatMoney(totals.count_difference_cost)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Manual adjustments</span>
          <strong>{formatMoney(totals.adjustment_cost)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Daily margin</span>
          <strong>{formatMoney(totals.margin)}</strong>
        </div>
      </div>

      <div className="rm-rates-card analyticsMatrixCard">
        <div className="rm-rates-header analyticsCompactHeader">
          <div>
            <h3 className="rm-rates-title">Daily margin</h3>
            <div className="rm-rates-subtitle">
              Sales, operational cost and MTD cost ratio by POS {currentLevel.label.toLowerCase()}
            </div>
          </div>
          <div className="analyticsSourceLegend">
            <span className="analyticsSourceBadge analyticsSourceBadge--actual">Actual</span>
            <span className="analyticsSourceBadge analyticsSourceBadge--forecast">Forecast</span>
            <span className="analyticsSourceBadge analyticsSourceBadge--mixed">Mixed</span>
          </div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {salesLoading ? "Loading matrix..." : `${marginRows.length} taxonomy rows`}
          </small>
        </div>

        <div className="rm-rates-table-wrap analyticsMatrixWrap">
          <table
            className="rm-rates-table analyticsMatrixTable"
            style={{
              "--rm-col1-width": "220px",
              "--rm-table-min-width": `${Math.max(1200, 360 + days.length * 132)}px`,
            }}
          >
            <colgroup>
              <col style={{ width: "220px" }} />
              {days.map((day) => (
                <col key={day.date} style={{ width: "132px" }} />
              ))}
              <col style={{ width: "140px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th rm-sticky-1">
                  <div className="res-thBtn">
                    <span className="res-thLabel">{currentLevel.label}</span>
                    <span className="res-thSub">POS taxonomy</span>
                  </div>
                </th>
                {days.map((day) => (
                  <th key={day.date} className="res-th">
                    <div className="res-thBtn analyticsDayHeader">
                      <span className="res-thLabel">{day.label}</span>
                      <span className="res-thSub">{month}</span>
                      <span className={`analyticsSourceBadge ${sourceClass(day.data_source)}`}>
                        {sourceLabel(day.data_source)}
                      </span>
                    </div>
                  </th>
                ))}
                <th className="res-th">
                  <div className="res-thBtn">
                    <span className="res-thLabel">Total</span>
                    <span className="res-thSub">Month</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {marginRows.map((row) => (
                <tr key={row.taxonomy_key || row.family_key}>
                  <td className="rm-sticky-1 analyticsFamilyName">
                    <div className="analyticsFamilyNameInner">
                      <strong>{row.taxonomy_name || row.family_name}</strong>
                      <span>{row.taxonomy_code || row.family_code}</span>
                    </div>
                  </td>
                  {days.map((day) => {
                    const cell = row.days?.[day.date] || {};
                    const hasMissingVat = Number(cell.missing_vat_lines || 0) > 0;
                    const isNegativeMargin = Number(cell.margin_mtd || 0) < 0;
                    return (
                      <td
                        key={day.date}
                        className={`analyticsMoneyCell analyticsMarginCell analyticsCostDetailCell ${
                          hasMissingVat ? "analyticsMoneyCell--warning" : ""
                        } analyticsCostDetailCell--${cell.data_source || "none"} ${
                          isNegativeMargin ? "analyticsMarginCell--negative" : ""
                        }`}
                        title={hasMissingVat ? `${cell.missing_vat_lines} lines without IVA` : undefined}
                      >
                        <span>
                          <b>Sales</b>
                          <strong>{compactMoney(cell.net_sales)}</strong>
                        </span>
                        <span>
                          <b>Sales MTD</b>
                          <strong>{compactMoney(cell.net_sales_mtd)}</strong>
                        </span>
                        <span>
                          <b>Cost</b>
                          <strong>{compactMoney(cell.purchase_cost)}</strong>
                        </span>
                        <span>
                          <b>Cost MTD</b>
                          <strong>{compactMoney(cell.purchase_cost_mtd)}</strong>
                        </span>
                        <span className="analyticsCountDiffLine">
                          <b>Count diff.</b>
                          <strong>{compactMoney(cell.count_difference_cost)}</strong>
                        </span>
                        <span className="analyticsAdjustmentLine">
                          <b>Manual adj.</b>
                          <strong>{compactMoney(cell.adjustment_cost)}</strong>
                        </span>
                        <em>
                          <b>Cost % MTD</b>
                          <strong>{formatPercent(cell.cost_percent_mtd)}</strong>
                        </em>
                      </td>
                    );
                  })}
                  <td
                    className={`analyticsMoneyCell analyticsMoneyCell--total analyticsMarginCell analyticsCostDetailCell analyticsCostDetailCell--total ${
                      Number(row.totals?.margin || 0) < 0 ? "analyticsMarginCell--negative" : ""
                    }`}
                  >
                    <span>
                      <b>Sales</b>
                      <strong>{compactMoney(row.totals?.net_sales)}</strong>
                    </span>
                    <span>
                      <b>Cost</b>
                      <strong>{compactMoney(row.totals?.purchase_cost)}</strong>
                    </span>
                    <span className="analyticsCountDiffLine">
                      <b>Count diff.</b>
                      <strong>{compactMoney(row.totals?.count_difference_cost)}</strong>
                    </span>
                    <span className="analyticsAdjustmentLine">
                      <b>Manual adj.</b>
                      <strong>{compactMoney(row.totals?.adjustment_cost)}</strong>
                    </span>
                    <em>
                      <b>Cost %</b>
                      <strong>{formatPercent(row.totals?.cost_percent)}</strong>
                    </em>
                  </td>
                </tr>
              ))}
              {!marginRows.length ? (
                <tr>
                  <td colSpan={days.length + 2} className="posMuted">
                    No sales found for this selection.
                  </td>
                </tr>
              ) : null}
              {marginRows.length ? (
                <tr className="analyticsTotalRow">
                  <td className="rm-sticky-1 analyticsFamilyName">
                    <div className="analyticsFamilyNameInner">
                      <strong>Total</strong>
                      <span>Selected taxonomy</span>
                    </div>
                  </td>
                  {days.map((day) => (
                    <td
                      key={day.date}
                      className={`analyticsMoneyCell analyticsMoneyCell--total analyticsMarginCell analyticsCostDetailCell ${
                        Number(totalsByDay[day.date]?.margin_mtd || 0) < 0 ? "analyticsMarginCell--negative" : ""
                      } analyticsCostDetailCell--${totalsByDay[day.date]?.data_source || "none"}`}
                    >
                      <span>
                        <b>Sales</b>
                        <strong>{compactMoney(totalsByDay[day.date]?.net_sales)}</strong>
                      </span>
                      <span>
                        <b>Sales MTD</b>
                        <strong>{compactMoney(totalsByDay[day.date]?.net_sales_mtd)}</strong>
                      </span>
                      <span>
                        <b>Cost</b>
                        <strong>{compactMoney(totalsByDay[day.date]?.purchase_cost)}</strong>
                      </span>
                      <span>
                        <b>Cost MTD</b>
                        <strong>{compactMoney(totalsByDay[day.date]?.purchase_cost_mtd)}</strong>
                      </span>
                      <span className="analyticsCountDiffLine">
                        <b>Count diff.</b>
                        <strong>{compactMoney(totalsByDay[day.date]?.count_difference_cost)}</strong>
                      </span>
                      <span className="analyticsAdjustmentLine">
                        <b>Manual adj.</b>
                        <strong>{compactMoney(totalsByDay[day.date]?.adjustment_cost)}</strong>
                      </span>
                      <em>
                        <b>Cost % MTD</b>
                        <strong>{formatPercent(totalsByDay[day.date]?.cost_percent_mtd)}</strong>
                      </em>
                    </td>
                  ))}
                  <td
                    className={`analyticsMoneyCell analyticsMoneyCell--total analyticsMarginCell analyticsCostDetailCell analyticsCostDetailCell--total ${
                      Number(totals.margin || 0) < 0 ? "analyticsMarginCell--negative" : ""
                    }`}
                  >
                    <span>
                      <b>Sales</b>
                      <strong>{compactMoney(totals.net_sales)}</strong>
                    </span>
                    <span>
                      <b>Cost</b>
                      <strong>{compactMoney(totals.purchase_cost)}</strong>
                    </span>
                    <span className="analyticsCountDiffLine">
                      <b>Count diff.</b>
                      <strong>{compactMoney(totals.count_difference_cost)}</strong>
                    </span>
                    <span className="analyticsAdjustmentLine">
                      <b>Manual adj.</b>
                      <strong>{compactMoney(totals.adjustment_cost)}</strong>
                    </span>
                    <em>
                      <b>Cost %</b>
                      <strong>{formatPercent(totals.cost_percent)}</strong>
                    </em>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
